import React, { useState, useEffect } from "react"
import Header from "../../../layouts/Header"
import { styled, createTheme, ThemeProvider } from "@mui/material/styles"
import { Box, Paper, Typography, Grid, Card, Stack, Snackbar, Tooltip, Button } from "@mui/material"
import { Home, Person, Assignment, CalendarToday, Gavel, Percent } from "@mui/icons-material"
import BackIcon from "../../../components/BackIcon"
import { useNavigate } from "react-router-dom"
import CustomBackdrop from "../../../components/CustomBackdrop"
import Swal from "sweetalert2"
import CustomAutocomplete from "../../../components/CustomAutocomplete"
import CustomTextFieldReadable from "../../../components/CustomTextFieldReadable"
import CustomTextField from "../../../components/CustomTextField"
import { useQuery, useMutation, api } from "../../../api"
import CrearIcon from "../../../assets/iconos/Crear.ico"

const StyledRoot = {
  width: "100%",
  maxWidth: "1400px",
  margin: "0 auto",
  padding: "20px",
  backgroundColor: "#f5f7fa",
  borderRadius: "12px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
}

const theme = createTheme({
  palette: {
    primary: {
      main: "#196C87",
    },
    secondary: {
      main: "#196C87",
    },
  },
})

const TIPO_DOCUMENTO_OPTIONS = [
  { value: "C", label: "Cédula" },
  { value: "R", label: "RUC" },
  { value: "P", label: "Pasaporte" },
]

const CrearDetalleATitularidad = () => {
  const navigate = useNavigate()
  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState("")
  const [errors, setErrors] = useState({})

  // API para generar el ID automáticamente
  const { data: idInscripcionData, isLoading: isLoadingId } = useQuery({
    queryKey: ["generateIdInscripcion"],
    url: "/ActivacionDePredios/generateIdInscripcion",
    enabled: true,
    onError: (error) => {
      alert("No se puedo generar el id de inscripción")
      console.error(`No se puedo generar el id de inscripción: ${error}`)
      navigate(-1)
    },
  })

  // API para obtener los títulos de adquisición
  const { data: titulosData, isLoading: isLoadingTitulos } = useQuery({
    queryKey: ["getTitulosAdquisicionCB"],
    url: "/ActivacionDePredios/getTitulosAdquisicionCB",
  })

  // API para obtener los predios
  const { data: prediosData, isLoading: isLoadingPredios } = useQuery({
    queryKey: ["getPrediosCB"],
    url: "/ActivacionDePredios/getPrediosCB",
  })

  // Estado inicial del formulario
  const [formData, setFormData] = useState({
    titid: "",
    preid: null,
    titadqpre: null,
    titfecha: new Date().toISOString().split("T")[0],
    tittipodocprop: null,
    titidpropietario: "",
    titnombresprop: "",
    titporcenacc: "100.00",
    titnotaria: "",
    titestado: "ACTIVO",
  })

  // Actualizar el ID cuando llegue de la API
  useEffect(() => {
    if (idInscripcionData?.data) {
      setFormData((prev) => ({
        ...prev,
        titid: idInscripcionData.data,
      }))
    }
  }, [idInscripcionData])

  // Validar formulario
  const validateForm = () => {
    const newErrors = {}

    if (!formData.titid.trim()) {
      newErrors.titid = "El ID de inscripción es requerido"
    }

    if (!formData.preid) {
      newErrors.preid = "Debe seleccionar un predio"
    }

    if (!formData.titadqpre) {
      newErrors.titadqpre = "Debe seleccionar un título de adquisición"
    }

    if (!formData.titfecha) {
      newErrors.titfecha = "La fecha de titularidad es requerida"
    }

    if (!formData.tittipodocprop) {
      newErrors.tittipodocprop = "Debe seleccionar el tipo de documento"
    }

    if (!formData.titidpropietario.trim()) {
      newErrors.titidpropietario = "El número de documento es requerido"
    } else if (formData.tittipodocprop?.value === "C" && !/^\d{10}$/.test(formData.titidpropietario)) {
      newErrors.titidpropietario = "La cédula debe tener 10 dígitos"
    } else if (formData.tittipodocprop?.value === "R" && !/^\d{13}$/.test(formData.titidpropietario)) {
      newErrors.titidpropietario = "El RUC debe tener 13 dígitos"
    }

    if (!formData.titnombresprop.trim()) {
      newErrors.titnombresprop = "Los nombres del propietario son requeridos"
    }

    const porcentaje = parseFloat(formData.titporcenacc)
    if (isNaN(porcentaje) || porcentaje <= 0 || porcentaje > 100) {
      newErrors.titporcenacc = "El porcentaje debe ser entre 0 y 100"
    }

    if (!formData.titnotaria.trim()) {
      newErrors.titnotaria = "El detalle de la notaría es requerido"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Manejar cambios en los campos
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }))

    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }))
    }
  }

  // Mutación para guardar
  const { mutate: saveTitularidad, isPending: isSaving } = useMutation({
    fn: async (data) => {
      const response = await api.post("/ActivacionDePredios/saveTitularidad", data)
      return response.data
    },
    showError: "modal",
    showSuccess: "silent",
    onSuccess: () => {
      Swal.fire({
        icon: "success",
        title: "¡Registrado con éxito!",
        text: "La titularidad ha sido registrada correctamente.",
        confirmButtonText: "OK",
      }).then(() => {
        navigate(-1)
      })
    },
  })

  // Manejar envío del formulario
  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      setSnackbarMessage("Por favor, complete todos los campos requeridos correctamente")
      setSnackbarOpen(true)
      return
    }

    const result = await Swal.fire({
      title: "Confirmar registro",
      text: "¿Está seguro de registrar esta titularidad?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sí, registrar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#196C87",
      allowOutsideClick: false,
      allowEscapeKey: false,
    })

    if (result.isConfirmed) {
      const dataToSave = {
        ...formData,
        preid: formData.preid?.value,
        titadqpre: formData.titadqpre?.value,
        tittipodocprop: formData.tittipodocprop?.value,
        titporcenacc: parseFloat(formData.titporcenacc),
        titfecha: new Date(formData.titfecha).toISOString(),
      }
      saveTitularidad(dataToSave)
    }
  }

  // Calcular loading
  const isLoading = isLoadingId || isLoadingTitulos || isLoadingPredios || isSaving

  // Extraer datos de las APIs
  const titulosList = titulosData?.data || []
  const prediosList = prediosData?.data || []

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 30px 30px 30px",
            fontSize: "25px",
          }}
        >
          <b>Registro de Titularidad de Predio</b>
        </div>
        <div>
          <Tooltip title="Registrar">
            <Button color="primary" onClick={handleSubmit} sx={{ marginBottom: "15px" }}>
              <img src={CrearIcon} alt="Registrar" style={{ width: "30px" }} />
            </Button>
          </Tooltip>
        </div>

        <CustomBackdrop isLoading={isLoading} />

        <Box sx={StyledRoot}>
          <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 3, background: "white" }} component="form">
            <Typography
              variant="h5"
              gutterBottom
              sx={{ color: "primary.main", mb: 3, display: "flex", alignItems: "center", gap: 1 }}
            >
              <Assignment /> Información de Titularidad
            </Typography>

            <Grid container spacing={3}>
              {/* Fila 1: ID y Estado */}
              <Grid item xs={12} md={3}>
                <CustomTextFieldReadable disabled label="ID Inscripción" value={formData.titid} />
                {isLoadingId && (
                  <Typography variant="caption" color="textSecondary">
                    Generando ID...
                  </Typography>
                )}
              </Grid>

              <Grid item xs={12} md={3}>
                <CustomTextFieldReadable disabled label="Estado" value={formData.titestado} />
              </Grid>

              {/* Fila 2: Comboboxes principales con datos de APIs */}
              <Grid item xs={12} md={6}>
                <CustomAutocomplete
                  label="Predio *"
                  selectedOption={formData.preid}
                  setSelectedOption={(option) => handleInputChange("preid", option)}
                  options={prediosList}
                  loading={isLoadingPredios}
                />
                {errors.preid && (
                  <Typography variant="caption" color="error">
                    {errors.preid}
                  </Typography>
                )}
              </Grid>

              <Grid item xs={12} md={6}>
                <CustomAutocomplete
                  label="Título de Adquisición *"
                  selectedOption={formData.titadqpre}
                  setSelectedOption={(option) => handleInputChange("titadqpre", option)}
                  options={titulosList}
                  loading={isLoadingTitulos}
                />
                {errors.titadqpre && (
                  <Typography variant="caption" color="error">
                    {errors.titadqpre}
                  </Typography>
                )}
              </Grid>

              {/* Fila 3: Fecha y Notaría */}
              <Grid item xs={12} md={6}>
                <CustomTextField
                  fullWidth
                  label="Fecha de Titularidad *"
                  type="date"
                  value={formData.titfecha}
                  onChange={(e) => handleInputChange("titfecha", e.target.value)}
                  error={!!errors.titfecha}
                  helperText={errors.titfecha}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <CustomTextField
                  fullWidth
                  label="Notaría *"
                  value={formData.titnotaria}
                  onChange={(e) => handleInputChange("titnotaria", e.target.value)}
                  error={!!errors.titnotaria}
                  helperText={errors.titnotaria}
                  placeholder="Ej: Notaría 24 del Cantón"
                />
              </Grid>

              {/* Sección de Propietario */}
              <Grid item xs={12}>
                <Typography
                  variant="h6"
                  sx={{ mt: 2, mb: 2, color: "primary.main", display: "flex", alignItems: "center", gap: 1 }}
                >
                  <Person /> Información del Propietario
                </Typography>
              </Grid>

              {/* Fila 4: Tipo Documento, Número y Porcentaje */}
              <Grid item xs={12} md={4}>
                <CustomAutocomplete
                  label="Tipo de Documento *"
                  selectedOption={formData.tittipodocprop}
                  setSelectedOption={(option) => handleInputChange("tittipodocprop", option)}
                  options={TIPO_DOCUMENTO_OPTIONS}
                />
                {errors.tittipodocprop && (
                  <Typography variant="caption" color="error">
                    {errors.tittipodocprop}
                  </Typography>
                )}
              </Grid>

              <Grid item xs={12} md={4}>
                <CustomTextField
                  fullWidth
                  label="Número de Documento *"
                  value={formData.titidpropietario}
                  onChange={(e) => handleInputChange("titidpropietario", e.target.value)}
                  error={!!errors.titidpropietario}
                  helperText={errors.titidpropietario}
                  inputProps={{
                    maxLength: formData.tittipodocprop?.value === "R" ? 13 : 10,
                  }}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <CustomTextField
                  fullWidth
                  label="Porcentaje de Acciones *"
                  type="number"
                  value={formData.titporcenacc}
                  onChange={(e) => handleInputChange("titporcenacc", e.target.value)}
                  error={!!errors.titporcenacc}
                  helperText={errors.titporcenacc}
                  inputProps={{ step: "0.01", min: 0, max: 100 }}
                />
              </Grid>

              {/* Fila 5: Nombres completos */}
              <Grid item xs={12}>
                <CustomTextField
                  fullWidth
                  label="Apellidos y Nombres del Propietario *"
                  value={formData.titnombresprop}
                  onChange={(e) => handleInputChange("titnombresprop", e.target.value)}
                  error={!!errors.titnombresprop}
                  helperText={errors.titnombresprop}
                  placeholder="Ej: Carlos Andrade y María Solís"
                />
              </Grid>

              {/* Nota informativa */}
              <Grid item xs={12}>
                <Card variant="outlined" sx={{ p: 2, bgcolor: "#f8f9fa" }}>
                  <Typography variant="body2" color="textSecondary">
                    <strong>Nota:</strong> El ID de inscripción se genera automáticamente desde el servidor. Para
                    copropietarios, se debe registrar cada titular por separado con su respectivo porcentaje de
                    acciones.
                  </Typography>
                </Card>
              </Grid>
            </Grid>
          </Paper>
        </Box>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={() => setSnackbarOpen(false)}
          message={snackbarMessage}
        />
      </div>
    </ThemeProvider>
  )
}

export default CrearDetalleATitularidad
